import { makeStyles, shorthands } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const HERO_TITLE_FONT_SIZE = "36px";
const SECTION_BADGE_SIZE = "44px";
const SECTION_ICON_SIZE = "22px";
const QUICK_LINK_ICON_CONTAINER_SIZE = "40px";

export const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor: projectIqTokens.bg,
    fontFamily: projectIqTokens.fontFamily,
  },

  body: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: designTokens.spacing[8],
  },

  content: {
    maxWidth: "1120px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[8],
  },

  hero: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  heroEyebrow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    color: projectIqTokens.teal,
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
  },

  heroEyebrowIcon: {
    flexShrink: 0,
  },

  heroTitle: {
    fontSize: HERO_TITLE_FONT_SIZE,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    letterSpacing: "-.02em",
    lineHeight: designTokens.lineHeight.tight,
    margin: 0,
  },

  heroDescription: {
    fontSize: designTokens.fontSize.sm,
    lineHeight: designTokens.lineHeight.normal,
    color: projectIqTokens.muted,
    margin: 0,
  },

  section: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
  },

  sectionBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: SECTION_BADGE_SIZE,
    height: SECTION_BADGE_SIZE,
    minWidth: SECTION_BADGE_SIZE,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.ink,
    color: designTokens.neutral.white,
    fontSize: SECTION_ICON_SIZE,
    flexShrink: 0,
  },

  sectionBadgeIcon: {
    fontSize: SECTION_ICON_SIZE,
  },

  sectionTitleGroup: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  sectionTitle: {
    fontSize: designTokens.fontSize.lg,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: 0,
    lineHeight: designTokens.lineHeight.tight,
  },

  sectionDescription: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    margin: 0,
  },

  sectionAction: {
    flexShrink: 0,
    marginLeft: "auto",
  },

  quickLinksGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: designTokens.spacing[4],
  },

  quickLinkCard: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[4]}`,
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: projectIqTokens.shadowSm,
    textDecoration: "none",
    color: "inherit",
    cursor: "pointer",
    "--arrow-color": projectIqTokens.faint,
    transition: `box-shadow ${designTokens.transition.fast}, transform ${designTokens.transition.fast}`,
    ":hover": {
      boxShadow: `0 0 0 1px ${projectIqTokens.tealLine}, ${projectIqTokens.shadow}`,
      transform: "translateY(-1px)",
      "--arrow-color": projectIqTokens.teal,
    },
  },

  quickLinkIconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: QUICK_LINK_ICON_CONTAINER_SIZE,
    height: QUICK_LINK_ICON_CONTAINER_SIZE,
    minWidth: QUICK_LINK_ICON_CONTAINER_SIZE,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.teal,
    fontSize: SECTION_ICON_SIZE,
    flexShrink: 0,
  },

  quickLinkContent: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  quickLinkTitle: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    margin: 0,
  },

  quickLinkSubtitle: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    margin: 0,
  },

  quickLinkArrow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--arrow-color)" as string,
    flexShrink: 0,
    alignSelf: "center",
  },

  quickLinkSkeletonIcon: {
    width: QUICK_LINK_ICON_CONTAINER_SIZE,
    height: QUICK_LINK_ICON_CONTAINER_SIZE,
    minWidth: QUICK_LINK_ICON_CONTAINER_SIZE,
    borderRadius: designTokens.borderRadius.md,
    flexShrink: 0,
  },

  quickLinkSkeletonTitle: {
    height: "14px",
    width: "65%",
    borderRadius: designTokens.borderRadius.sm,
    marginBottom: "4px",
  },

  quickLinkSkeletonSubtitle: {
    height: "12px",
    width: "45%",
    borderRadius: designTokens.borderRadius.sm,
  },

  newAnalyticsButton: {
    ...shorthands.borderRadius(designTokens.borderRadius.md),
    backgroundColor: projectIqTokens.teal,
    color: designTokens.neutral.white,
    fontWeight: designTokens.fontWeight.bold,
    fontSize: designTokens.fontSize.sm,
    fontFamily: projectIqTokens.fontFamily,
    paddingTop: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[2],
    paddingLeft: designTokens.spacing[4],
    paddingRight: designTokens.spacing[4],
    ":hover": {
      backgroundColor: projectIqTokens.tealInk,
      color: designTokens.neutral.white,
    },
    ":active": {
      backgroundColor: projectIqTokens.tealInk,
      color: designTokens.neutral.white,
    },
  },

  analyticsControls: {
    display: "flex",
    flexDirection: "row",
    gap: designTokens.spacing[3],
    marginBottom: designTokens.spacing[4],
  },

  searchWrapper: {
    flex: "1 1 0%",
    position: "relative",
  },

  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: projectIqTokens.faint,
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
  },

  searchNativeInput: {
    width: "100%",
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: "9px",
    padding: `9px 13px 9px 36px`,
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.ink,
    backgroundColor: projectIqTokens.card,
    fontFamily: projectIqTokens.fontFamily,
    outline: "none",
    ":focus": {
      border: `1px solid ${projectIqTokens.teal}`,
    },
    "::-webkit-search-cancel-button": {
      cursor: "pointer",
    },
  },

  sortWrapper: {
    flex: "0 0 auto",
  },

  sortTrigger: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: "9px 13px",
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: "9px",
    backgroundColor: projectIqTokens.card,
    color: projectIqTokens.ink,
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    fontFamily: projectIqTokens.fontFamily,
    cursor: "pointer",
    whiteSpace: "nowrap",
    ":hover": {
      border: `1px solid ${projectIqTokens.teal}`,
    },
  },

  sortTriggerText: {
    color: projectIqTokens.ink,
  },

  sortTriggerChevron: {
    color: projectIqTokens.muted,
    flexShrink: 0,
    transition: `transform ${designTokens.transition.fast}`,
  },

  sortTriggerChevronOpen: {
    transform: "rotate(180deg)",
  },

  sortPopoverSurface: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: "9px",
    boxShadow: projectIqTokens.shadow,
    minWidth: "170px",
    padding: "5px",
    display: "flex",
    flexDirection: "column",
  },

  sortMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    width: "100%",
    textAlign: "left",
    padding: "7px 10px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: designTokens.fontWeight.semibold,
    border: "none",
    cursor: "pointer",
    backgroundColor: "transparent",
    color: projectIqTokens.ink,
    fontFamily: projectIqTokens.fontFamily,
    ":hover": {
      backgroundColor: projectIqTokens.bg2,
    },
  },

  sortMenuItemActive: {
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.tealInk,
    fontWeight: designTokens.fontWeight.bold,
    ":hover": {
      backgroundColor: projectIqTokens.tealSoft,
    },
  },

  sortMenuItemLabel: {
    flex: 1,
  },

  analyticsList: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },

  skeletonCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: projectIqTokens.shadowSm,
  },

  skeletonCardInner: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "16px",
    padding: "18px",
  },

  skeletonCardIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "11px",
    flexShrink: 0,
  },

  skeletonCardBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },

  skeletonCardNameRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },

  skeletonCardName: {
    height: "14px",
    width: "55%",
  },

  skeletonCardPill: {
    height: "14px",
    width: "60px",
    borderRadius: "5px",
  },

  skeletonCardDescription: {
    height: "12px",
    width: "75%",
  },

  skeletonCardMeta: {
    height: "12px",
    width: "120px",
  },

  skeletonCardArrow: {
    width: "16px",
    height: "16px",
    flexShrink: 0,
  },

  emptyState: {
    textAlign: "center",
    padding: designTokens.spacing[8],
    color: projectIqTokens.muted,
    fontSize: designTokens.fontSize.sm,
  },

  exportErrorBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[3],
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    marginBottom: designTokens.spacing[3],
    backgroundColor: projectIqTokens.rustBg,
    color: projectIqTokens.rust,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
  },

  exportErrorDismiss: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: projectIqTokens.rust,
    fontSize: designTokens.fontSize.base,
    padding: "0",
    lineHeight: "1",
    flexShrink: 0,
  },
});
